import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, TrendingUp,
  Send, ShieldCheck, Sparkles, Target,
  Activity, Zap, Database,
} from "lucide-react";
import USDemandMap from "@/components/USDemandMap";
import RoiCalculator from "@/components/RoiCalculator";

export default function Landing() {
  const location = useLocation();

  useEffect(() => {
    document.title = "LumberLoop USA — Lead Intelligence SaaS Platform for Manufacturers";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "Stop losing generated opportunities. LumberLoop USA is the industry's first lead intelligence platform for building product manufacturers — bridging the gap between your website, your dealers, and your revenue.");
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, [location.hash, location.pathname]);

  return (
    <div data-testid="landing-page" className="bg-white text-[#17324D]">
      <Hero />
      <Problem />
      <Solution />
      <Benefits />
      <RoiCalculator />
      <SocialProof />
      <FinalCTA />
    </div>
  );
}

/* ============ SECTION 1 — HERO ============ */
function Hero() {
  const bullets = [
    { title: "Total Visibility",              body: "See exactly how your dealers handle every single lead." },
    { title: "Automatic Follow-Up",           body: "Dealer opens a simple email and responds — LumberLoop USA automatically handles the rest." },
    { title: "Live Lead Status Updates",      body: "See in real time — dealer response time, contacted, won, lost, estimate, and so much more." },
  ];
  return (
    <section id="top" className="relative overflow-hidden bg-white scroll-mt-24" data-testid="hero">
      <div className="absolute inset-0 grid-backdrop opacity-70" aria-hidden="true"/>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="reveal-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm">
            <span className="w-1.5 h-1.5 bg-[#C62828] rounded-full animate-pulse"/>
            <span className="font-mono-ll text-[10px] tracking-[0.24em] uppercase text-[#17324D] font-semibold">Your Leads · Direct To The Right Dealer(s) · Automatically</span>
          </div>

          <h1
            className="font-display text-[#17324D] mt-6 leading-[1.02]"
            data-testid="hero-headline"
          >
            <span className="block text-4xl sm:text-5xl lg:text-6xl">Turn Your Website Inquiries</span>
            <span className="block text-4xl sm:text-5xl lg:text-6xl mt-1">Into <span className="text-[#C62828]">Measurable Dealer Sales.</span></span>
          </h1>

          <p className="mt-6 text-lg text-slate-700 max-w-xl leading-relaxed font-body" data-testid="hero-subhead">
            LumberLoop USA allows building product manufacturers to capture contractor demand, connect opportunities with the right dealers in their market, and track contacts, quotes, follow-ups, and outcomes — all while <strong className="text-[#17324D]">keeping the dealer experience simple</strong>.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-cta" data-testid="hero-cta-demo">
              Request Early-Access Demo <ArrowRight size={16}/>
            </Link>
            <a href="#solution" className="btn-secondary" data-testid="hero-cta-how">
              See Platform
            </a>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {bullets.map((b) => (
              <div key={b.title} className="flex items-start gap-2.5" data-testid={`hero-bullet-${b.title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="mt-0.5 w-5 h-5 rounded-full bg-[#C62828] text-white flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={12} strokeWidth={3}/>
                </div>
                <div>
                  <div className="font-heading text-sm text-[#17324D] leading-tight">{b.title}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-snug font-body">{b.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <HeroDashboardMock/>
        </div>
      </div>
    </section>
  );
}

function HeroDashboardMock() {
  return (
    <div className="relative reveal-up" style={{ animationDelay: "0.2s" }}>
      <div className="hidden lg:block absolute -top-6 -left-4 z-20 bg-white rounded-lg border border-slate-200 shadow-xl shadow-slate-900/10 p-3 pr-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-[#C62828]/10 text-[#C62828] flex items-center justify-center"><Activity size={14}/></div>
          <div>
            <div className="text-[10px] font-mono-ll tracking-wider uppercase text-slate-400">Live Demand</div>
            <div className="text-sm font-bold text-[#17324D]">+14 today</div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block absolute -bottom-6 -right-4 z-20 bg-white rounded-lg border border-slate-200 shadow-xl shadow-slate-900/10 p-3 pr-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-[#17324D]/10 text-[#17324D] flex items-center justify-center"><TrendingUp size={14}/></div>
          <div>
            <div className="text-[10px] font-mono-ll tracking-wider uppercase text-slate-400">Win Rate</div>
            <div className="text-sm font-bold text-[#17324D]">31% <span className="text-xs text-emerald-600 font-mono-ll">+6%</span></div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400"/>
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400"/>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"/>
          </div>
          <div className="font-mono-ll text-[10px] tracking-wider text-slate-400">lumberloop.app/dashboard</div>
          <div className="w-8"/>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono-ll text-[10px] tracking-[0.24em] uppercase text-slate-400">Manufacturer Command Center</div>
              <div className="font-heading text-[#17324D] text-lg mt-0.5">This Quarter</div>
            </div>
            <div className="text-[10px] font-mono-ll tracking-wider text-emerald-600 bg-emerald-50 px-2 py-1 rounded">● Live</div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <MiniKPI label="Requests" value="127" delta="+14%"/>
            <MiniKPI label="Response" value="3.2h" delta="-22%"/>
            <MiniKPI label="Conv." value="31%" delta="+6%"/>
          </div>

          <div className="mt-3">
            <USDemandMap height={220} compact title="Demand by State · 30d"/>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniKPI({ label, value, delta }) {
  return (
    <div className="rounded-lg border border-slate-200 p-2.5 bg-white">
      <div className="text-[9px] font-mono-ll tracking-wider uppercase text-slate-400">{label}</div>
      <div className="font-display text-lg text-[#17324D] mt-0.5 leading-none">{value}</div>
      <div className="text-[9px] font-mono-ll text-emerald-600 mt-1">{delta}</div>
    </div>
  );
}

/* ============ SECTION 2 — THE PROBLEM (BLACK HOLE) ============ */
function Problem() {
  return (
    <section id="problem" className="bg-slate-50 border-t border-slate-100 py-24 scroll-mt-24" data-testid="problem">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <div className="label-tag justify-center">The Visibility Gap</div>
        <h2 className="font-display text-3xl md:text-5xl lg:text-6xl mt-5 leading-[1.05] text-[#17324D]">
          More Visitors Will Not Fix<br/>
          an Invisible Process…<br/>
          Or Create a <span className="text-[#C62828]">Return On Investment.</span>
        </h2>
        <p className="mt-8 text-lg md:text-xl text-slate-700 leading-relaxed font-body max-w-3xl mx-auto">
          Before investing more money into lead generation, manufacturers need to understand what&apos;s happening to the opportunities they already create. Without clear visibility after an opportunity visits your site, it&apos;s nearly impossible to know what happened after the opportunity was handed to your dealer.
        </p>
        <p className="mt-4 text-lg md:text-xl text-slate-700 leading-relaxed font-body max-w-3xl mx-auto">
          LumberLoop USA gives <span className="font-semibold text-[#17324D]">every opportunity</span> a visible next step — from contractor, to dealer contact, quote, follow-up, and outcome.
        </p>
      </div>
    </section>
  );
}

/* ============ SECTION 3 — THE SOLUTION (CAPTURE · ROUTE · TRACK) ============ */
function Solution() {
  const steps = [
    {
      n: "01",
      icon: Send,
      title: "Capture",
      pill: "Lead In",
      body: "We catch the lead the moment it lands on your site — every product inquiry, every quote request, every high-intent contractor.",
    },
    {
      n: "02",
      icon: Zap,
      title: "Route",
      pill: "Auto-Match",
      body: "We push it to the right approved dealer in the right territory — instantly, automatically, based on rules you control.",
    },
    {
      n: "03",
      icon: ShieldCheck,
      title: "Track",
      pill: "Live Status",
      body: "We track follow-ups so you can see response time, quote status, and outcomes.",
    },
  ];

  const [active, setActive] = React.useState(0);
  const [playing, setPlaying] = React.useState(true);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setActive((a) => (a + 1) % steps.length), 2000);
    return () => clearInterval(id);
  }, [playing, steps.length]);

  React.useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { threshold: 0.25 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="solution" className="relative bg-[#0A1B30] py-24 lg:py-28 border-t border-slate-800 scroll-mt-24 overflow-hidden" data-testid="solution">
      {/* Background: grid + red radial glow + tech dots */}
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
        backgroundSize: "56px 56px",
      }} aria-hidden="true"/>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-[#C62828]/12 blur-3xl -z-0" aria-hidden="true"/>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C62828]/60 to-transparent" aria-hidden="true"/>

      <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C62828]" style={{ animation: "sol-blink 1.4s ease-in-out infinite" }}/>
            <span className="font-tech-mono text-[10px] tracking-[0.28em] uppercase text-slate-300 font-semibold">The Solution · Live Workflow</span>
          </div>
          <h2 className="font-tech font-bold text-4xl md:text-6xl lg:text-7xl mt-6 leading-[0.98] text-white">
            <span className="text-white">Capture.</span>{" "}
            <span className="text-white">Route.</span>{" "}
            <span className="text-[#C62828]">Track.</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-300 font-body max-w-2xl mx-auto">
            Dealers update opportunities directly from email — <span className="text-white font-semibold">no complicated portal required.</span>
          </p>
        </div>

        {/* Flow cards */}
        <div ref={wrapRef} className="mt-16 relative">
          {/* Beam connecting cards (behind) */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 -translate-y-1/2 z-0 pointer-events-none" style={{ height: 2 }}>
            <div className="mx-auto h-full max-w-[calc(100%-8rem)] bg-white/8"/>
            <div
              className="absolute top-1/2 left-0 h-3 w-3 -translate-y-1/2 rounded-full bg-[#C62828]"
              style={{
                boxShadow: "0 0 0 6px rgba(198,40,40,0.18), 0 0 24px rgba(198,40,40,0.7)",
                animation: "sol-beam 6s linear infinite",
              }}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-5 lg:gap-6 relative z-10">
            {steps.map((s, i) => {
              const isActive = active === i;
              const isDone = i < active;
              return (
                <div
                  key={s.n}
                  onMouseEnter={() => { setPlaying(false); setActive(i); }}
                  onMouseLeave={() => setPlaying(true)}
                  className={`group relative rounded-2xl p-7 lg:p-8 transition-all duration-500 cursor-default overflow-hidden ${
                    isActive
                      ? "bg-[#122744] border border-[#C62828]/60 shadow-[0_30px_60px_-12px_rgba(198,40,40,0.35)] -translate-y-1"
                      : "bg-white/[0.03] border border-white/8 hover:border-white/20 hover:bg-white/[0.05]"
                  }`}
                  data-testid={`solution-${s.title.toLowerCase()}`}
                >
                  {/* Gigantic step number as background art */}
                  <div
                    className={`absolute -right-2 -top-6 font-tech font-bold leading-none select-none transition-all duration-500 ${
                      isActive ? "text-[#C62828]/22" : "text-white/[0.04]"
                    }`}
                    style={{ fontSize: 180, letterSpacing: "-0.05em" }}
                    aria-hidden="true"
                  >
                    {s.n}
                  </div>

                  {/* Live/status pill */}
                  <div className="relative flex items-center gap-2">
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[9px] font-tech-mono font-semibold uppercase tracking-[0.16em] transition-all duration-500 ${
                      isActive ? "bg-[#C62828]/20 border-[#C62828]/50 text-[#FCA5A5]" : "bg-white/5 border-white/10 text-slate-400"
                    }`}>
                      <span className={`w-1 h-1 rounded-full ${isActive ? "bg-[#C62828]" : "bg-slate-500"}`} style={isActive ? { animation: "sol-blink 1.2s ease-in-out infinite" } : {}}/>
                      {isActive ? "Live" : (isDone ? "Done" : "Standby")}
                    </div>
                    <span className="font-tech-mono text-[10px] tracking-widest uppercase text-slate-500">{s.pill}</span>
                  </div>

                  {/* Icon */}
                  <div className="relative mt-6">
                    <div
                      className={`w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-500 ${
                        isActive
                          ? "bg-[#C62828] text-white scale-110 shadow-[0_0_40px_-4px_rgba(198,40,40,0.9)]"
                          : "bg-white/8 text-white/70"
                      }`}
                    >
                      <s.icon size={26} strokeWidth={2}/>
                    </div>
                    {isActive && (
                      <span className="absolute inset-0 w-16 h-16 rounded-xl bg-[#C62828] opacity-40 pointer-events-none" aria-hidden="true"
                            style={{ animation: "sol-ripple 2s ease-out infinite" }}/>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className={`font-tech font-bold text-3xl md:text-4xl mt-6 tracking-tight transition-colors duration-500 ${
                    isActive ? "text-white" : "text-white/90"
                  }`}>
                    {s.title}
                    <span className="inline-block ml-1 w-1.5 h-6 md:h-7 align-middle" style={{
                      background: isActive ? "#C62828" : "transparent",
                      animation: isActive ? "sol-caret 0.9s steps(2) infinite" : "none",
                    }} aria-hidden="true"/>
                  </h3>

                  {/* Body */}
                  <p className={`mt-3 leading-relaxed font-body transition-colors duration-500 ${
                    isActive ? "text-slate-200" : "text-slate-400"
                  }`}>
                    {s.body}
                  </p>

                  {/* Arrow node between cards */}
                  {i < steps.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-4 -translate-y-1/2 z-20 w-8 h-8 rounded-full items-center justify-center border transition-all duration-500 backdrop-blur-sm"
                         style={{
                           background: isActive || isDone ? "#C62828" : "#0A1B30",
                           borderColor: isActive || isDone ? "#C62828" : "rgba(255,255,255,0.15)",
                           boxShadow: isActive || isDone ? "0 0 20px rgba(198,40,40,0.5)" : "none",
                         }}>
                      <ArrowRight size={14} className={isActive || isDone ? "text-white" : "text-white/50"}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div className="mt-10 mx-auto max-w-md">
            <div className="flex items-center justify-between mb-2">
              <span className="font-tech-mono text-[10px] tracking-[0.24em] uppercase text-slate-500">Workflow</span>
              <span className="font-tech-mono text-[10px] tracking-[0.24em] uppercase text-[#FCA5A5]">
                {String(active + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}
              </span>
            </div>
            <div className="h-[3px] rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#C62828] to-[#F87171] transition-all duration-700 ease-out"
                style={{ width: `${((active + 1) / steps.length) * 100}%`, boxShadow: "0 0 12px rgba(198,40,40,0.7)" }}
              />
            </div>
          </div>

          <style>{`
            @keyframes sol-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
            @keyframes sol-ripple {
              0%   { transform: scale(1);    opacity: 0.4; }
              100% { transform: scale(1.5);  opacity: 0;   }
            }
            @keyframes sol-caret { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
            @keyframes sol-beam {
              0%   { transform: translateX(0)    translateY(-50%); opacity: 0; }
              10%  { opacity: 1; }
              100% { transform: translateX(100%) translateY(-50%); opacity: 0; }
            }
          `}</style>
        </div>
      </div>
    </section>
  );
}

/* ============ SECTION 4 — BENEFITS ============ */
function Benefits() {
  const items = [
    {
      icon: Target,
      title: "Increase conversion rates",
      body: "by tracking dealer follow-up — no more leads dying in a dealer inbox.",
    },
    {
      icon: TrendingUp,
      title: "Get real-time data on market demand",
      body: "by product, region, and dealer — the intelligence you've never had.",
    },
    {
      icon: Database,
      title: "Integrate seamlessly with your existing CRM",
      body: "so your sales team keeps the tools they know and the data stays clean.",
    },
  ];
  return (
    <section id="benefits" className="bg-slate-50 py-24 border-t border-slate-100 scroll-mt-24" data-testid="benefits">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="label-tag justify-center">Why It Matters</div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-[1.05] text-[#17324D]">
            The outcomes you&apos;ve been <span className="text-[#C62828]">missing.</span>
          </h2>
        </div>

        <div className="mt-14 space-y-4">
          {items.map((it, i) => (
            <div key={it.title} className="rounded-xl bg-white border border-slate-200 p-6 md:p-7 flex items-start gap-5 hover:border-[#17324D]/30 hover:shadow-md transition-all" data-testid={`benefit-${i+1}`}>
              <div className="w-12 h-12 rounded-lg bg-[#C62828]/10 text-[#C62828] flex items-center justify-center flex-shrink-0">
                <it.icon size={22}/>
              </div>
              <div>
                <div className="font-heading text-xl md:text-2xl text-[#17324D]">{it.title}</div>
                <div className="text-slate-700 mt-1 font-body">{it.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ SECTION 5 — SOCIAL PROOF ============ */
function SocialProof() {
  return (
    <section className="bg-white py-14 border-t border-slate-100" data-testid="social-proof">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
          <Sparkles size={12} className="text-[#C62828]"/>
          <span className="font-mono-ll text-[10px] tracking-[0.24em] uppercase text-[#17324D] font-semibold">Built by Industry</span>
        </div>
        <p className="font-heading text-xl md:text-2xl text-[#17324D] mt-5 leading-snug">
          Built by industry professionals<br className="sm:hidden"/> for the <span className="text-[#C62828]">modern manufacturer.</span>
        </p>
      </div>
    </section>
  );
}

/* ============ SECTION 6 — FINAL CTA ============ */
function FinalCTA() {
  return (
    <section id="request-demo" className="relative bg-white border-t border-slate-200 scroll-mt-24" data-testid="final-cta">
      <div className="absolute inset-0 grid-backdrop opacity-40" aria-hidden="true"/>

      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
          <Sparkles size={12} className="text-[#C62828]"/>
          <span className="font-mono-ll text-[10px] tracking-[0.24em] uppercase text-[#17324D] font-semibold">Ready When You Are</span>
        </div>

        <h2 className="font-display text-[#17324D] text-3xl md:text-5xl lg:text-6xl mt-6 leading-[1.02]">
          Stop losing what you already <span className="text-[#C62828]">paid to generate.</span>
        </h2>

        <p className="mt-6 text-lg text-slate-700 max-w-2xl mx-auto font-body">
          A brief walkthrough with your product categories and dealer footprint mapped in.
        </p>

        <div className="mt-10">
          <Link to="/contact" className="btn-cta" data-testid="final-cta-demo">
            Request Early-Access Demo <ArrowRight size={16}/>
          </Link>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500 font-body">
          <a href="mailto:info@lumberloopusa.com" className="hover:text-[#C62828] transition-colors font-mono-ll">info@lumberloopusa.com</a>
        </div>
      </div>
    </section>
  );
}
